import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardActionArea,
  Chip,
  Collapse,
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import { SAGE, CREAM } from '../theme';
import { mockAuthHeaders } from '../utils/mockAuth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const JobFamilyHubPage = () => {
  const navigate = useNavigate();
  const [taxonomy, setTaxonomy] = useState(null);
  const [jobCounts, setJobCounts] = useState({});
  const [totalJobs, setTotalJobs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedDisc, setSelectedDisc] = useState(null);

  useEffect(() => {
    const headers = { ...mockAuthHeaders() };

    // Load taxonomy first (single S3 GET, fast) — page renders immediately
    // with structure even before job counts arrive
    fetch(`${API_URL}/api/taxonomy`, { headers, credentials: 'include' })
      .then((r) => r.json())
      .then((taxData) => setTaxonomy(taxData))
      .catch((err) => console.error('Failed to load taxonomy:', err))
      .finally(() => setLoading(false));

    // Load job counts separately. Hits the fast artifact endpoint that
    // reads a pre-aggregated stats file (1 S3 GET); falls through to
    // the slow live aggregation if the artifact isn't present yet.
    fetch(`${API_URL}/api/job-families/stats`, { headers, credentials: 'include' })
      .then((r) => r.json())
      .then((familiesData) => {
        const counts = {};
        let total = 0;
        for (const f of familiesData.families || []) {
          counts[f.family_id] = {
            total: f.total_postings,
            seattle: f.seattle_count,
            remote: f.remote_market_count,
            companies: f.company_count,
          };
          total += f.total_postings;
        }
        setJobCounts(counts);
        setTotalJobs(total);
      })
      .catch((err) => console.error('Failed to load family counts:', err));
  }, []);

  const getDiscCounts = (disc) => {
    const candidates = [
      disc.id,
      disc.name.toLowerCase().replace(/ /g, '_').replace(/&/g, '').replace(/__/g, '_'),
      disc.name.toLowerCase().replace(/ /g, '_'),
    ];
    for (const key of candidates) {
      if (jobCounts[key]) return jobCounts[key];
    }
    return null;
  };

  const getDeptTotal = (deptId) => {
    if (!taxonomy) return 0;
    const discs = taxonomy.disciplines.filter((d) => d.department_ids.includes(deptId));
    let total = 0;
    const counted = new Set();
    for (const disc of discs) {
      if (counted.has(disc.id)) continue;
      counted.add(disc.id);
      const counts = getDiscCounts(disc);
      if (counts) total += counts.total;
    }
    return total;
  };

  const handleDeptClick = (deptId) => {
    if (selectedDept === deptId) {
      // Collapse everything
      setSelectedDept(null);
      setSelectedDisc(null);
    } else {
      setSelectedDept(deptId);
      setSelectedDisc(null);
    }
  };

  const handleDiscClick = (discId) => {
    setSelectedDisc(selectedDisc === discId ? null : discId);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: SAGE[500] }} />
      </Box>
    );
  }

  if (!taxonomy || !taxonomy.departments) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <WorkIcon sx={{ fontSize: 48, color: SAGE[300], mb: 2 }} />
        <Typography sx={{ color: SAGE[500], fontFamily: "'Outfit', sans-serif" }}>
          Taxonomy not available — check S3 configuration
        </Typography>
      </Box>
    );
  }

  const disciplines = selectedDept
    ? taxonomy.disciplines.filter((d) => d.department_ids.includes(selectedDept))
    : [];

  const roles = selectedDisc
    ? taxonomy.roles.filter((r) => r.discipline_id === selectedDisc)
    : [];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: SAGE[900], mb: 0.5 }}
        >
          Explore Roles
        </Typography>
        <Typography variant="body2" sx={{ color: SAGE[500] }}>
          {taxonomy.roles.length} roles across {taxonomy.disciplines.length} disciplines
          {totalJobs > 0 && ` \u00b7 ${totalJobs.toLocaleString()} active postings`}
        </Typography>
      </Box>

      {/* Step 1: Department cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
          gap: 1.5,
          mb: 0,
        }}
      >
        {taxonomy.departments.map((dept) => {
          const isSelected = selectedDept === dept.id;
          const deptTotal = getDeptTotal(dept.id);
          const discCount = taxonomy.disciplines.filter((d) =>
            d.department_ids.includes(dept.id)
          ).length;

          return (
            <Card
              key={dept.id}
              elevation={0}
              sx={{
                border: `2px solid ${isSelected ? SAGE[600] : SAGE[200]}`,
                borderRadius: 2.5,
                bgcolor: isSelected ? SAGE[800] : CREAM[50],
                transition: 'all 0.15s',
                '&:hover': isSelected
                  ? {}
                  : { borderColor: SAGE[400], boxShadow: `0 2px 8px ${SAGE[200]}` },
              }}
            >
              <CardActionArea
                onClick={() => handleDeptClick(dept.id)}
                sx={{ p: 2.5 }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    color: isSelected ? CREAM[50] : SAGE[900],
                    lineHeight: 1.3,
                    mb: 0.5,
                  }}
                >
                  {dept.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.73rem',
                    color: isSelected ? `${CREAM[50]}B0` : SAGE[400],
                    fontFamily: "'Outfit', sans-serif",
                    lineHeight: 1.4,
                    mb: 1.5,
                    minHeight: 32,
                  }}
                >
                  {dept.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontFamily: "'Outfit', sans-serif",
                      color: isSelected ? `${CREAM[50]}90` : SAGE[400],
                    }}
                  >
                    {discCount} disciplines
                  </Typography>
                  {deptTotal > 0 && (
                    <Chip
                      label={`${deptTotal}`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        fontFamily: "'Outfit', sans-serif",
                        bgcolor: isSelected ? `${CREAM[50]}20` : SAGE[100],
                        color: isSelected ? `${CREAM[50]}C0` : SAGE[500],
                      }}
                    />
                  )}
                </Box>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>

      {/* Step 2: Disciplines (expand below departments) */}
      <Collapse in={!!selectedDept} timeout={250}>
        <Box sx={{ mt: 2.5, mb: 0 }}>
          <Typography
            sx={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 600,
              fontSize: '0.78rem',
              color: SAGE[400],
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              mb: 1.5,
            }}
          >
            Disciplines in {taxonomy.departments.find((d) => d.id === selectedDept)?.name}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: 1.5,
            }}
          >
            {disciplines.map((disc) => {
              const isSelected = selectedDisc === disc.id;
              const counts = getDiscCounts(disc);
              const isMultiHomed = disc.department_ids.length > 1;
              const roleCount = taxonomy.roles.filter(
                (r) => r.discipline_id === disc.id
              ).length;

              return (
                <Card
                  key={disc.id}
                  elevation={0}
                  sx={{
                    border: `2px solid ${isSelected ? SAGE[500] : SAGE[200]}`,
                    borderRadius: 2,
                    bgcolor: isSelected ? `${SAGE[100]}` : CREAM[50],
                    transition: 'all 0.15s',
                    '&:hover': isSelected
                      ? {}
                      : { borderColor: SAGE[300], boxShadow: `0 1px 6px ${SAGE[200]}` },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleDiscClick(disc.id)}
                    sx={{ p: 2 }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography
                        sx={{
                          fontFamily: "'Outfit', sans-serif",
                          fontWeight: 600,
                          fontSize: '0.88rem',
                          color: SAGE[800],
                          lineHeight: 1.3,
                          flex: 1,
                        }}
                      >
                        {disc.name}
                      </Typography>
                      {counts && (
                        <Chip
                          label={counts.total}
                          size="small"
                          sx={{
                            height: 20,
                            minWidth: 28,
                            fontSize: '0.65rem',
                            fontFamily: "'Outfit', sans-serif",
                            bgcolor: SAGE[100],
                            color: SAGE[500],
                            ml: 1,
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '0.73rem',
                        color: SAGE[400],
                        fontFamily: "'Outfit', sans-serif",
                        lineHeight: 1.4,
                        mt: 0.5,
                      }}
                    >
                      {disc.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                      <Typography
                        sx={{ fontSize: '0.68rem', color: SAGE[400], fontFamily: "'Outfit', sans-serif" }}
                      >
                        {roleCount} roles
                      </Typography>
                      {isMultiHomed && (
                        <Chip
                          label={`also in ${disc.department_ids
                            .filter((id) => id !== selectedDept)
                            .map((id) => taxonomy.departments.find((d) => d.id === id)?.name || id)
                            .join(', ')}`}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: '0.58rem',
                            fontFamily: "'Outfit', sans-serif",
                            bgcolor: `${SAGE[100]}80`,
                            color: SAGE[400],
                          }}
                        />
                      )}
                    </Box>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>
        </Box>
      </Collapse>

      {/* Step 3: Roles (expand below disciplines) */}
      <Collapse in={!!selectedDisc} timeout={250}>
        <Box sx={{ mt: 2.5 }}>
          <Typography
            sx={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 600,
              fontSize: '0.78rem',
              color: SAGE[400],
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              mb: 1.5,
            }}
          >
            Roles in {taxonomy.disciplines.find((d) => d.id === selectedDisc)?.name}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 1.5,
            }}
          >
            {roles.map((role) => (
              <Card
                key={role.id}
                elevation={0}
                sx={{
                  border: `1px solid ${SAGE[200]}`,
                  borderRadius: 2,
                  bgcolor: CREAM[50],
                  transition: 'all 0.15s',
                  '&:hover': {
                    borderColor: SAGE[400],
                    boxShadow: `0 2px 8px ${SAGE[200]}`,
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <CardActionArea
                  onClick={() => navigate(`/explore/roles/${selectedDisc}?role=${role.id}`)}
                  sx={{ p: 2 }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 600,
                      fontSize: '0.88rem',
                      color: SAGE[900],
                      lineHeight: 1.3,
                      mb: 0.5,
                    }}
                  >
                    {role.name}
                  </Typography>
                  {role.description && (
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        color: SAGE[400],
                        fontFamily: "'Outfit', sans-serif",
                        lineHeight: 1.4,
                      }}
                    >
                      {role.description}
                    </Typography>
                  )}
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default JobFamilyHubPage;
